import React, { useState } from 'react';
import { 
  Mail, 
  Phone, 
  MessageSquare, 
  Calendar, 
  DollarSign, 
  FileX, 
  Scale,
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
  User,
  Download
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils/currencyUtils';

interface OverdueActionsProps {
  selectedTransactions: Set<string>;
  onActionComplete: () => void;
  disabled?: boolean;
  totalAmount?: number;
  clientCount?: number;
}

interface ActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  children: React.ReactNode;
  onConfirm: () => void;
  confirmLabel?: string;
  isLoading?: boolean;
}

function ActionDialog({ 
  open, 
  onOpenChange, 
  title, 
  description, 
  children, 
  onConfirm, 
  confirmLabel = "Confirmar",
  isLoading = false 
}: ActionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {children}
          
          <div className="flex justify-end gap-3">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button 
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {confirmLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function OverdueActions({
  selectedTransactions,
  onActionComplete,
  disabled = false,
  totalAmount = 0,
  clientCount = 0
}: OverdueActionsProps) {
  // Dialog states
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [paymentPlanDialogOpen, setPaymentPlanDialogOpen] = useState(false);
  const [writeOffDialogOpen, setWriteOffDialogOpen] = useState(false);
  
  // Loading states
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Form states
  const [emailTemplate, setEmailTemplate] = useState('cobranca_padrao');
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState(5);
  const [discountReason, setDiscountReason] = useState('');
  const [paymentPlanInstallments, setPaymentPlanInstallments] = useState(3);
  const [writeOffReason, setWriteOffReason] = useState('');
  const [sendNotification, setSendNotification] = useState(true);


  const handleEmailAction = async () => {
    setIsProcessing(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success(`Email de cobrança enviado para ${clientCount} cliente(s)`);
      setEmailDialogOpen(false);
      onActionComplete();
    } catch (error) {
      toast.error('Erro ao enviar emails');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleWhatsAppAction = async () => {
    setIsProcessing(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast.success(`WhatsApp enviado para ${clientCount} cliente(s)`);
      setWhatsappDialogOpen(false);
      onActionComplete();
    } catch (error) {
      toast.error('Erro ao enviar WhatsApp');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDiscountAction = async () => {
    setIsProcessing(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const discountAmount = totalAmount * (discountPercentage / 100);
      toast.success(`Desconto de ${formatCurrency(discountAmount)} aplicado`);
      setDiscountDialogOpen(false);
      onActionComplete();
    } catch (error) {
      toast.error('Erro ao aplicar desconto');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaymentPlanAction = async () => {
    setIsProcessing(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success(`Plano de pagamento criado para ${selectedTransactions.size} lançamento(s)`);
      setPaymentPlanDialogOpen(false);
      onActionComplete();
    } catch (error) {
      toast.error('Erro ao criar plano de pagamento');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleWriteOffAction = async () => {
    setIsProcessing(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast.success(`${selectedTransactions.size} lançamento(s) baixado(s)`);
      setWriteOffDialogOpen(false);
      onActionComplete();
    } catch (error) {
      toast.error('Erro ao dar baixa');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportSelected = () => {
    toast.success(`Exportando dados de ${selectedTransactions.size} lançamento(s)`);
  };

  if (selectedTransactions.size === 0) {
    return (
      <div className="flex items-center justify-center p-8 border border-dashed rounded-lg">
        <div className="text-center">
          <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nenhum lançamento selecionado</h3>
          <p className="text-sm text-muted-foreground">
            Selecione um ou mais lançamentos para executar ações em lote
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Selection Summary */}
      <div className="flex items-center justify-between p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <CheckCircle className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold">
              {selectedTransactions.size} lançamento(s) selecionado(s)
            </h3>
            <p className="text-sm text-muted-foreground">
              {clientCount} cliente(s) • Total: {formatCurrency(totalAmount)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant="secondary">
            {selectedTransactions.size} itens
          </Badge>
        </div>
      </div>

      {/* Action Buttons */}
      <TooltipProvider>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Email Cobrança */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                onClick={() => setEmailDialogOpen(true)}
                disabled={true}
                className="h-auto p-4 flex flex-col items-center gap-2"
              >
                <Mail className="h-5 w-5" />
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">Email Cobrança</span>
                  <Badge variant="outline" className="text-xs text-foreground">
                    Em breve
                  </Badge>
                </div>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Funcionalidade em desenvolvimento: Email de cobrança</p>
            </TooltipContent>
          </Tooltip>

          {/* WhatsApp */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                onClick={() => setWhatsappDialogOpen(true)}
                disabled={true}
                className="h-auto p-4 flex flex-col items-center gap-2"
              >
                <MessageSquare className="h-5 w-5" />
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">WhatsApp</span>
                  <Badge variant="outline" className="text-xs text-foreground">
                    Em breve
                  </Badge>
                </div>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Funcionalidade em desenvolvimento: WhatsApp de cobrança</p>
            </TooltipContent>
          </Tooltip>

          {/* Plano de Pagamento */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                onClick={() => setPaymentPlanDialogOpen(true)}
                disabled={true}
                className="h-auto p-4 flex flex-col items-center gap-2"
              >
                <Calendar className="h-5 w-5" />
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">Plano Pagamento</span>
                  <Badge variant="outline" className="text-xs text-foreground">
                    Em breve
                  </Badge>
                </div>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Funcionalidade em desenvolvimento: Plano de pagamento</p>
            </TooltipContent>
          </Tooltip>

          {/* Aplicar Desconto */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                onClick={() => setDiscountDialogOpen(true)}
                disabled={disabled}
                className="h-auto p-4 flex flex-col items-center gap-2"
              >
                <DollarSign className="h-5 w-5" />
                <span className="text-sm">Aplicar Desconto</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Simulado: Não persiste no banco de dados</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>

      {/* Secondary Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportSelected}
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar Selecionados
          </Button>
        </div>

        <TooltipProvider>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                Mais Ações
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Ações Avançadas</DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={() => toast.info('Agendar ligação em desenvolvimento')}>
                <Phone className="h-4 w-4 mr-2" />
                Agendar Ligação
              </DropdownMenuItem>

              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuItem onClick={() => setWriteOffDialogOpen(true)}>
                    <FileX className="h-4 w-4 mr-2" />
                    Dar Baixa
                  </DropdownMenuItem>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Simulado: Não persiste no banco de dados</p>
                </TooltipContent>
              </Tooltip>

              <DropdownMenuItem onClick={() => toast.info('Enviar para jurídico em desenvolvimento')}>
                <Scale className="h-4 w-4 mr-2" />
                Enviar para Jurídico
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TooltipProvider>
      </div>

      {/* Email Dialog */}
      <ActionDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        title="Enviar Email de Cobrança"
        description={`Enviar email para ${clientCount} cliente(s) selecionado(s)`}
        onConfirm={handleEmailAction}
        confirmLabel="Enviar Emails"
        isLoading={isProcessing}
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="email-template">Template de Email</Label>
            <select 
              id="email-template"
              value={emailTemplate}
              onChange={(e) => setEmailTemplate(e.target.value)}
              className="w-full mt-1 p-2 border rounded-md bg-background text-foreground"
            >
              <option value="cobranca_padrao">Cobrança Padrão</option>
              <option value="cobranca_educada">Cobrança Educada</option>
              <option value="ultima_chance">Última Chance</option>
              <option value="personalizado">Personalizado</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="send-notification"
              checked={sendNotification}
              onCheckedChange={setSendNotification}
            />
            <Label htmlFor="send-notification" className="text-sm">
              Enviar notificação de leitura
            </Label>
          </div>

          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm">
              <strong>Resumo:</strong> {selectedTransactions.size} lançamento(s) • {formatCurrency(totalAmount)}
            </p>
          </div>
        </div>
      </ActionDialog>

      {/* WhatsApp Dialog */}
      <ActionDialog
        open={whatsappDialogOpen}
        onOpenChange={setWhatsappDialogOpen}
        title="Enviar WhatsApp"
        description={`Enviar mensagem via WhatsApp para ${clientCount} cliente(s)`}
        onConfirm={handleWhatsAppAction}
        confirmLabel="Enviar WhatsApp"
        isLoading={isProcessing}
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="whatsapp-message">Mensagem</Label>
            <Textarea
              id="whatsapp-message"
              placeholder="Digite a mensagem de cobrança..."
              value={whatsappMessage}
              onChange={(e) => setWhatsappMessage(e.target.value)}
              rows={4}
            />
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
              <p className="text-sm text-amber-800">
                Apenas clientes com WhatsApp cadastrado receberão a mensagem.
              </p>
            </div>
          </div>
        </div>
      </ActionDialog>

      {/* Discount Dialog */}
      <ActionDialog
        open={discountDialogOpen}
        onOpenChange={setDiscountDialogOpen}
        title="Aplicar Desconto"
        description={`Aplicar desconto nos ${selectedTransactions.size} lançamento(s) selecionado(s)`}
        onConfirm={handleDiscountAction}
        confirmLabel="Aplicar Desconto"
        isLoading={isProcessing}
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="discount-percentage">Percentual de Desconto (%)</Label>
            <Input
              id="discount-percentage"
              type="number"
              min="0"
              max="100"
              value={discountPercentage}
              onChange={(e) => setDiscountPercentage(Number(e.target.value))}
            />
          </div>

          <div>
            <Label htmlFor="discount-reason">Motivo do Desconto</Label>
            <Input
              id="discount-reason"
              placeholder="Ex: Acordo de quitação"
              value={discountReason}
              onChange={(e) => setDiscountReason(e.target.value)}
            />
          </div>

          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm">
              <strong>Valor original:</strong> {formatCurrency(totalAmount)}<br/>
              <strong>Desconto:</strong> {formatCurrency(totalAmount * (discountPercentage / 100))}<br/>
              <strong>Valor final:</strong> {formatCurrency(totalAmount * (1 - discountPercentage / 100))}
            </p>
          </div>
        </div>
      </ActionDialog>

      {/* Payment Plan Dialog */}
      <ActionDialog
        open={paymentPlanDialogOpen}
        onOpenChange={setPaymentPlanDialogOpen}
        title="Criar Plano de Pagamento"
        description={`Criar plano de pagamento para ${selectedTransactions.size} lançamento(s)`}
        onConfirm={handlePaymentPlanAction}
        confirmLabel="Criar Plano"
        isLoading={isProcessing}
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="installments">Número de Parcelas</Label>
            <Input
              id="installments"
              type="number"
              min="2"
              max="24"
              value={paymentPlanInstallments}
              onChange={(e) => setPaymentPlanInstallments(Number(e.target.value))}
            />
          </div>

          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm">
              <strong>Valor total:</strong> {formatCurrency(totalAmount)}<br/>
              <strong>Valor por parcela:</strong> {formatCurrency(totalAmount / paymentPlanInstallments)}<br/>
              <strong>Primeira parcela:</strong> Hoje + 30 dias
            </p>
          </div>
        </div>
      </ActionDialog>

      {/* Write Off Dialog */}
      <ActionDialog
        open={writeOffDialogOpen}
        onOpenChange={setWriteOffDialogOpen}
        title="Dar Baixa nos Lançamentos"
        description={`Esta ação irá dar baixa definitiva em ${selectedTransactions.size} lançamento(s)`}
        onConfirm={handleWriteOffAction}
        confirmLabel="Confirmar Baixa"
        isLoading={isProcessing}
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="writeoff-reason">Motivo da Baixa</Label>
            <Textarea
              id="writeoff-reason"
              placeholder="Ex: Cliente sem condições de pagamento"
              value={writeOffReason}
              onChange={(e) => setWriteOffReason(e.target.value)}
              rows={3}
            />
          </div>

          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
              <div>
                <p className="text-sm text-red-800 font-medium">
                  Atenção: Esta ação é irreversível!
                </p>
                <p className="text-sm text-red-700">
                  Os lançamentos serão marcados como baixa definitiva e não poderão ser recuperados.
                </p>
              </div>
            </div>
          </div>
        </div>
      </ActionDialog>
    </div>
  );
}